import { Regex } from './regex';
import moment from 'moment';

export class Tools {
    static hexToRgbA(hex) {
        var c;
        if (/([A-Fa-f0-9]{3}){1,2}$/.test(hex) && !/^#/.test(hex)) {
            hex = '#' + hex;
        }
        if (/^#([A-Fa-f0-9]{3}){1,2}$/.test(hex)) {
            c = hex.substring(1).split('');
            if (c.length === 3) {
                c = [c[0], c[0], c[1], c[1], c[2], c[2]];
            }
            c = '0x' + c.join('');
            return 'rgba(' + [(c >> 16) & 255, (c >> 8) & 255, c & 255].join(',') + ',1)';
        }
    }

    /**
     * Returns a localised string matching the format: HH:mm
     * @param {Date} date The date object to get the string from
     */
    static getTimeStringFromDate(date) {
        const dateObj = moment(date);
        let returnStr = "";

        returnStr += dateObj.hours();
        returnStr += ":";
        if (dateObj.minutes() === 0) {
            returnStr += "00"
        } else {
            returnStr += dateObj.minutes();
        }

        return returnStr;
    }

    /**
     * Returns a string matching the format YYYY-MM-DD
     * @param {Date} date The object to extract the string from
     */
    static getDayStringFromDate(date) {
        const dateObj = new Date(date);
        let returnStr = "";

        returnStr += dateObj.getFullYear();
        returnStr += "-";
        let month = dateObj.getMonth() + 1;
        if (month.toString().length === 1) {
            month = "0" + month;
        }
        returnStr += month;
        returnStr += "-";
        let day = dateObj.getDate();
        if (day.toString().length === 1) {
            day = "0" + day;
        }
        returnStr += day;

        return returnStr;
    }

    /**
     * Replaces the day in the date string in the standard time format
     * @param {String} dayString The new day string matching the format YYYY-MM-DD
     * @param {String} date The string date to put the new day into, matching "YYYY-MM-DDTHH:mm:ss.000Z"
     */
    static changeDayStringInDateString(dayString, date) {
        if (Regex.dayString.test(dayString)) {
            date = date.replace(Regex.dayString, dayString)
        } else {
            date = null;
        }
        return date;
    }

    /**
     * Creates a standard UTC time String mathing YYYY-MM-DDTHH:mm:ss.000Z
     * @param {String} dayString A String matching the format YYYY-MM-DD
     * @param {String} timeString A String matching the format HH:mm
     */
    static createDateStringFromFormComponents(dayString, timeString) {
        let returnStr = "";
        returnStr += dayString;
        returnStr += "T";
        returnStr += timeString;
        returnStr += ":00.000";
        let date = moment(returnStr);

        return date.utc().format();
    }

    /**
     * Returns true if the two dates are happening on the same day, false otherwise.
     * @param {Date} date1 The first date to compare
     * @param {Date} date2 The second date to compare
     */
    static datesAreTheSameDay(date1, date2) {
        const date1String = Tools.getDayStringFromDate(date1);
        const date2String = Tools.getDayStringFromDate(date2);

        if (date1String === date2String) {
            return true;
        } else {
            return false;
        }
    }

    /**
     * Calendar date (YYYY-MM-DD) for a task timestamp.
     * Task times are stored as wall-clock values in UTC ISO strings (see TaskCard, edit forms).
     */
    static getTaskCalendarDayString(isoDateString) {
        return moment.utc(isoDateString).format('YYYY-MM-DD');
    }

    /**
     * Parses a stored task ISO string into a Date for react-datepicker (wall-clock, not TZ-shifted).
     */
    static parseTaskToPickerDate(isoDateString) {
        return moment(String(isoDateString).replace('Z', '')).toDate();
    }

    /**
     * Interprets picker values and API strings as wall-clock moments (YYYY-MM-DDTHH:mm:ss in UTC).
     */
    static taskWallClockMoment(isoOrDate) {
        if (isoOrDate instanceof Date) {
            return moment.utc(moment(isoOrDate).format('YYYY-MM-DDTHH:mm:ss'));
        }
        return moment.utc(String(isoOrDate).replace('Z', ''));
    }

    /**
     * When end time is at or before start time, the task ends on the next calendar day (e.g. 22:00–04:00).
     * When end time is after start time on the start day, the task stays on that single day (e.g. 20:00–21:00).
     */
    static normalizeTaskWallClockRange(start, end) {
        const startM = Tools.taskWallClockMoment(start);
        let endM = Tools.taskWallClockMoment(end);
        const endOnStartDay = startM.clone()
            .hour(endM.hour())
            .minute(endM.minute())
            .second(endM.second());

        if (endOnStartDay.isAfter(startM)) {
            endM = endOnStartDay;
        } else if (endM.isSameOrBefore(startM)) {
            endM = endM.clone().add(1, 'day');
        }

        return {
            start_date: startM.format(),
            end_date: endM.format(),
        };
    }

    /**
     * Formats picker dates for the API using the wall-clock-as-UTC convention.
     */
    static formatTaskDatesForApi(startDate, endDate) {
        return Tools.normalizeTaskWallClockRange(startDate, endDate);
    }

    /**
     * Ensures picker end dates reflect overnight tasks (end on the day after start when needed).
     */
    static adjustEndDateForPicker(startDate, endDate) {
        const normalized = Tools.normalizeTaskWallClockRange(startDate, endDate);
        return Tools.parseTaskToPickerDate(normalized.end_date);
    }

    /**
     * True when `calDay` is any calendar day from the task start through end (inclusive).
     * Overnight tasks (e.g. 22:00–04:00) match both the start day and the end day.
     */
    static taskOccursOnCalendarDay(task, calDay) {
        const day = Tools.getDayStringFromDate(calDay);
        const normalized = Tools.normalizeTaskWallClockRange(task.start_date, task.end_date);
        const startDay = Tools.getTaskCalendarDayString(normalized.start_date);
        const endDay = Tools.getTaskCalendarDayString(normalized.end_date);
        return day >= startDay && day <= endDay;
    }

    /**
     * Returns the two first uppercased letters of a String
     * @param {String} str The name of the user to get the initials from
     */
    static getInitials(str) {
        let split = str.split(' ');
        let result = "";

        split.forEach((str, index) => {
            if (index < 2) {
                result += str.charAt(0).toUpperCase();
            }
        });

        return result;
    }

    /**
     * Looks for digit at the end of the name and adds a 0 in front of single digit.
     * ---
     * `Grupp 1` becomes `Grupp 01` and therefore will be sorted properly:
     * - Grupp 1
     * - Grupp 2
     * - Grupp 10
     * Instead of the alphabetical
     * - Grupp 1
     * - Grupp 10
     * - Grupp 2
     * @param {String} str The name of the group
     */
    static prepareGroupNameForSorting(str) {
        let result = ""
        let re = Regex.endingWithSingleDigit
        let match = str.match(re)

        if (match == null) {
            result = str
        } else {
            result = match.input.replace(match[0], ' 0' + match[0].trim())
        }

        return result
    }

    static getCookie(name) {
        let result = '';
        let cookies = document.cookie.split(';');

        cookies.forEach((cookie) => {
            let cookieParts = cookie.split('=');

            if (cookieParts[0].trim() === name) {
                result = cookieParts[1];
            }
        });

        return result;
    }

    static getMonday(d) {
        d = new Date(d);
        let day = d.getDay();
        let diff = d.getDate() - day + (day === 0 ? -6 : 1);
        return new Date(d.setDate(diff));
    }

    /**
     * Normalises organisation.first_weekday to 1–7 (1=Monday … 7=Sunday).
     * @param {object} organisation User organisation object or null
     * @returns {number}
     */
    static firstWeekdayFromOrganisation(organisation) {
        const raw = organisation && organisation.first_weekday;
        if (raw === null || raw === undefined) {
            return 1;
        }
        const n = Number(raw);
        if (Number.isFinite(n) && n >= 1 && n <= 7) {
            return n;
        }
        return 1;
    }

    /**
     * Week start for the schedule grid and tasks/week API, using org settings.
     * @param {Date} date Anchor date (any day in the intended week)
     * @param {object} organisation User organisation or null (defaults to Monday start)
     */
    static getVisibleWeekStart(date, organisation) {
        const fw = Tools.firstWeekdayFromOrganisation(organisation);
        return Tools.getWeekStart(date, fw);
    }

    /**
     * Returns the start of the week based on the specified first day of week
     * @param {Date} date The date from which to calculate week start
     * @param {number} firstWeekday The first day of week (1=Monday, 2=Tuesday, ..., 7=Sunday)
     */
    static getWeekStart(date, firstWeekday = 1) {
        const d = new Date(date);
        const currentDay = d.getDay();
        const targetFirstDay = firstWeekday === 7 ? 0 : firstWeekday;
        
        let daysToSubtract;
        if (currentDay >= targetFirstDay) {
            daysToSubtract = currentDay - targetFirstDay;
        } else {
            daysToSubtract = currentDay + (7 - targetFirstDay);
        }
        
        d.setDate(d.getDate() - daysToSubtract);
        return d;
    }

    /**
     * Difference in whole calendar days between two local dates (time ignored).
     * @param {Date} a
     * @param {Date} b
     * @returns {number} a - b in days
     */
    static _calendarDaysBetween(a, b) {
        const ua = Date.UTC(a.getFullYear(), a.getMonth(), a.getDate());
        const ub = Date.UTC(b.getFullYear(), b.getMonth(), b.getDate());
        return Math.round((ua - ub) / 86400000);
    }

    /**
     * Calendar week number (1-based) for the organisation's first day of week.
     * Week 1 is the week that contains Jan 1 of the chosen calendar year.
     * @param {Date} d Anchor date (any day in the intended week)
     * @param {number} firstWeekday 1=Monday … 7=Sunday (same as getWeekStart)
     * @param {number} [calendarYear] If set, week 1 is relative to this calendar year (for grids spanning late Dec / early Jan)
     * @returns {number}
     */
    static getWeekNumberForDate(d, firstWeekday = 1, calendarYear) {
        const Y = calendarYear !== undefined && calendarYear !== null ? calendarYear : d.getFullYear();
        const week1Start = Tools.getWeekStart(new Date(Y, 0, 1), firstWeekday);
        const weekStart = Tools.getWeekStart(d, firstWeekday);
        const diffDays = Tools._calendarDaysBetween(weekStart, week1Start);
        return 1 + Math.floor(diffDays / 7);
    }

    /**
     * First calendar month (1–12) in `year` that intersects the week starting at `weekStart`.
     * Used so week 1 around Jan 1 appears under January even if the week begins in December.
     * @param {Date} weekStart
     * @param {number} year
     * @returns {number}
     */
    static _firstMonthInYearForWeek(weekStart, year) {
        for (let i = 0; i < 7; i++) {
            const t = new Date(weekStart);
            t.setDate(t.getDate() + i);
            if (t.getFullYear() === year) {
                return t.getMonth() + 1;
            }
        }
        return weekStart.getMonth() + 1;
    }

    /**
     * Week numbers grouped by calendar month for the Groups weeks UI.
     * @param {number} year Calendar year
     * @param {number} firstWeekday 1=Monday … 7=Sunday
     * @returns {Object.<number, string[]>} keys 1–12, values sorted week labels "01"…"53"
     */
    static getWeekNumberInMonths(year, firstWeekday = 1) {
        const weeks = {};
        for (let index = 1; index <= 12; index++) {
            weeks[index] = [];
        }

        let ws = Tools.getWeekStart(new Date(year, 0, 1), firstWeekday);
        const seen = new Set();
        let guard = 0;

        while (Tools._weekIntersectsYear(ws, year) && guard < 60) {
            guard += 1;
            const anyDayInYear = Tools._firstDayOfWeekInCalendarYear(ws, year);
            const wn = Tools.getWeekNumberForDate(anyDayInYear, firstWeekday, year);
            const monthIndex = Tools._firstMonthInYearForWeek(ws, year);
            const label = wn < 10 ? `0${wn}` : String(wn);
            const key = `${monthIndex}-${label}`;
            if (!seen.has(key)) {
                seen.add(key);
                if (weeks[monthIndex] && !weeks[monthIndex].includes(label)) {
                    weeks[monthIndex].push(label);
                }
            }

            const nextWs = new Date(ws);
            nextWs.setDate(nextWs.getDate() + 7);
            ws = nextWs;
        }

        for (let m = 1; m <= 12; m++) {
            weeks[m].sort((a, b) => Number(a) - Number(b));
        }

        return weeks;
    }

    static _weekIntersectsYear(weekStart, year) {
        for (let i = 0; i < 7; i++) {
            const t = new Date(weekStart);
            t.setDate(t.getDate() + i);
            if (t.getFullYear() === year) {
                return true;
            }
        }
        return false;
    }

    static _firstDayOfWeekInCalendarYear(weekStart, year) {
        for (let i = 0; i < 7; i++) {
            const t = new Date(weekStart);
            t.setDate(t.getDate() + i);
            if (t.getFullYear() === year) {
                return t;
            }
        }
        return weekStart;
    }

    static sortArrayByObjectKey(array, key) {
        array.sort((a, b) => {
            if (a[key] > b[key]) {
                return 1;
            }

            if (b[key] > a[key]) {
                return -1;
            }

            return 0;
        });
    }

    static sortingInAlphabetic(a, b, item = "", order = "asc") {
        let wordA = "";
        let wordB = "";

        if (item !== "") {
            wordA = a[item].toLowerCase();
            wordB = b[item].toLowerCase();
        } else {
            wordA = a.toLowerCase();
            wordB = b.toLowerCase();
        }

        if (wordA < wordB) {
            if (order === "asc") {
                return -1;
            }

            return 1;
        }

        if (wordA > wordB) {
            if (order === "asc") {
                return 1;
            }

            return -1;
        }

        return 0;
    }
}